import { useState, useEffect } from 'react';
import { useCanvasStore } from '@/stores/canvasStore';
import { UserAvatar } from '@/components/UserAvatar';
import { UserBadges } from '@/components/UserBadges';
import { UsernameText } from '@/components/UsernameText';

// Detect if device supports hover (has a mouse/trackpad)
function useHasHover(): boolean {
  const [hasHover, setHasHover] = useState(true);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(hover: hover)');
    setHasHover(mediaQuery.matches);

    const handler = (e: MediaQueryListEvent) => setHasHover(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);

  return hasHover;
}

function formatTimeAgo(dateString: string): string {
  // SQLite datetime('now') stores UTC without 'Z' suffix, so JS interprets as local time
  // Append 'Z' if not present to ensure UTC parsing
  const normalizedDate = dateString.endsWith('Z') ? dateString : dateString.replace(' ', 'T') + 'Z';
  const date = new Date(normalizedDate);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffSecs = Math.floor(diffMs / 1000);
  const diffMins = Math.floor(diffSecs / 60);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSecs < 60) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

interface PixelInfoProps {
  pixel?: { x: number; y: number } | null;
  idleLabel?: string;
  followHover?: boolean;
  statusText?: string | undefined;
}

export function PixelInfo({ pixel, idleLabel, followHover = true, statusText }: PixelInfoProps) {
  const hoveredPixel = useCanvasStore((state) => state.hoveredPixel);
  const pixels = useCanvasStore((state) => state.pixels);
  const getPixelMeta = useCanvasStore((state) => state.getPixelMeta);
  const hasHover = useHasHover();
  const targetPixel = pixel ?? (followHover ? hoveredPixel : null);
  const emptyState = idleLabel ?? (hasHover ? 'Hover over the canvas' : 'Tap a pixel to inspect');

  if (!targetPixel) {
    return (
      <div className="bg-wall-surface/88 backdrop-blur-md border border-wall-border/60 rounded-2xl px-4 py-1.5 shadow-lg w-[min(94vw,760px)] overflow-hidden">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
          <span className="text-[11px] uppercase tracking-wide text-wall-muted">Inspect</span>
          <span className="text-wall-border/80" aria-hidden="true">
            •
          </span>
          <span className="text-[11px] uppercase tracking-wide text-wall-muted">{emptyState}</span>
        </div>
      </div>
    );
  }

  const color = pixels[targetPixel.y]?.[targetPixel.x];
  const meta = getPixelMeta(targetPixel.x, targetPixel.y);

  return (
    <div className="bg-wall-surface/88 backdrop-blur-md border border-wall-border/60 rounded-2xl px-4 py-1.5 shadow-lg w-[min(94vw,760px)] overflow-hidden">
      <div className="flex flex-col gap-1">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 text-sm">
          {statusText && (
            <>
              <span className="text-[11px] uppercase tracking-wide text-wall-accent font-semibold">
                {statusText}
              </span>
              <span className="text-wall-border/80" aria-hidden="true">
                •
              </span>
            </>
          )}

          <span className="text-[11px] uppercase tracking-wide text-wall-muted">Position</span>
          <span className="font-mono text-wall-text">
            ({targetPixel.x}, {targetPixel.y})
          </span>

          <span className="text-wall-border/80" aria-hidden="true">
            •
          </span>

          {color ? (
            <>
              <span className="text-[11px] uppercase tracking-wide text-wall-muted">Color</span>
              <div
                className="w-4 h-4 rounded-sm border border-wall-border/60"
                style={{ backgroundColor: color }}
              />
              <span className="font-mono text-xs text-wall-text">{color}</span>
            </>
          ) : (
            <span className="text-xs text-wall-muted italic">Empty pixel</span>
          )}
        </div>

        {meta && (
          <div className="flex items-center gap-2 min-w-0 pt-1 border-t border-wall-border/35">
            {meta.userId ? (
              <UserAvatar userId={meta.userId} username={meta.username} className="h-6 w-6" />
            ) : (
              <span className="h-6 w-6 shrink-0 rounded-full overflow-hidden bg-wall-border/50 flex items-center justify-center">
                <span className="text-xs font-medium text-wall-muted leading-none">
                  {meta.username.slice(0, 2).toUpperCase()}
                </span>
              </span>
            )}
            <div className="flex items-center gap-2 min-w-0">
              <UsernameText
                text={meta.username}
                color={meta.usernameColor ?? null}
                effect={meta.usernameEffect ?? null}
                className="text-xs font-medium text-wall-highlight truncate max-w-[240px]"
              />
              {meta.userId && <UserBadges userId={meta.userId} />}
              <span className="text-xs text-wall-muted whitespace-nowrap">
                {formatTimeAgo(meta.placedAt)}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
